import React, { createContext, useContext, useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import type {
  PlanInputRow, ReadinessSession, ReleaseRecord,
  ResolvedRequirement, MoldReadinessRow, ReadinessReason, UnmappedItem, MoldReadiness,
  SendRecord, SendSnapshot, PlanDelta,
} from '@/types/readiness';
import type { Mold, PMPlan } from '@/types/maintenance';
import type { MoldSparePart, SparePart } from '@/types/spareParts';
import { getAvailableStock } from '@/types/spareParts';
import { useWorkOrders } from '@/stores/workOrderStore';
import { useSpareParts } from '@/stores/sparePartsStore';
import { useAuth } from '@/contexts/AuthContext';
import { fetchMolds, fetchPMPlans } from '@/lib/api/molds';
import {
  fetchReadinessSession, upsertReadinessSession,
  fetchReleases, createRelease, updateRelease,
  fetchSendRecords, createSendRecord,
} from '@/lib/api/readiness';
import { computeMoldStatus } from '@/components/mold/MoldComputedStatus';
import { computePMDue, getLocationDisplay } from '@/lib/moldListUtils';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
const isUUID = (s: string) => UUID_RE.test(s);

// ─── localStorage keys ────────────────────────────────────────────────────────
const LS_SESSION    = 'readiness_session';
const LS_MOLD_ROWS  = 'readiness_mold_rows';
const LS_UNMAPPED   = 'readiness_unmapped';
const LS_RELEASES   = 'readiness_releases';
const LS_SEND_LOGS  = 'readiness_send_logs';

// Prefix keys with orgId so different orgs never share the same localStorage slots
function lsKey(base: string, orgId: string | null): string {
  return orgId ? `${base}_${orgId}` : base;
}

function loadLS<T>(key: string, orgId: string | null): T | null {
  try { return JSON.parse(localStorage.getItem(lsKey(key, orgId)) || 'null'); } catch { return null; }
}
function saveLS(key: string, orgId: string | null, value: unknown) {
  try { localStorage.setItem(lsKey(key, orgId), JSON.stringify(value)); } catch { /* quota */ }
}
function clearLS(orgId: string | null) {
  [LS_SESSION, LS_MOLD_ROWS, LS_UNMAPPED, LS_RELEASES, LS_SEND_LOGS].forEach(k => localStorage.removeItem(lsKey(k, orgId)));
}

// ─── Hash utility ─────────────────────────────────────────────────────────────
function hashInputRows(rows: PlanInputRow[]): string {
  return rows
    .map(r => `${r.part_number}|${r.resolved_mold_id || ''}|${r.requested_date}`)
    .sort().join(';;');
}

// ─── Context type ─────────────────────────────────────────────────────────────
interface ReadinessContextType {
  session: ReadinessSession | null;
  releases: Map<string, ReleaseRecord>;
  moldRows: MoldReadinessRow[];
  unmappedItems: UnmappedItem[];
  sendLogs: Map<string, SendRecord[]>;
  previousMoldRows: MoldReadinessRow[];
  moldsLoading: boolean;
  molds: Mold[];
  // Map<mold_id, dates[]> — molds appearing on 2+ different dates
  moldConflicts: Map<string, string[]>;
  generateRequirements: (rows: PlanInputRow[]) => void;
  clearSession: () => void;
  releaseForDate: (date: string) => void;
  needsRereleaseForDate: (date: string) => boolean;
  sendReadinessForDate: (date: string, sendAnywayReason?: string) => void;
  hasReadinessChangedSinceSend: (date: string) => boolean;
  getSendLogsForDate: (date: string) => SendRecord[];
  getPlanDelta: () => PlanDelta | null;
}

const ReadinessContext = createContext<ReadinessContextType | null>(null);

// ─── Provider ─────────────────────────────────────────────────────────────────
export function ReadinessProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const { getWorkOrdersByMold } = useWorkOrders();
  const { getPartsByMold } = useSpareParts();
  const orgId = user?.organization_id ?? null;

  // Fetch molds and pm_plans from Supabase (reuses cached data from Molds page)
  const { data: molds = [], isLoading: moldsLoading } = useQuery({
    queryKey: ['molds', orgId],
    queryFn: () => fetchMolds(orgId),
    enabled: !!user,
    staleTime: 5 * 60_000,
  });

  const { data: pmPlans = [] } = useQuery({
    queryKey: ['pm_plans', orgId],
    queryFn: () => fetchPMPlans(orgId),
    enabled: !!user,
    staleTime: 5 * 60_000,
  });

  // ── State — restored from localStorage on mount ────────────────────────────
  const [session, setSession] = useState<ReadinessSession | null>(() => loadLS(LS_SESSION, orgId));
  const [moldRowsState, setMoldRowsState] = useState<MoldReadinessRow[]>(() => loadLS(LS_MOLD_ROWS, orgId) ?? []);
  const [unmappedItemsState, setUnmappedItemsState] = useState<UnmappedItem[]>(() => loadLS(LS_UNMAPPED, orgId) ?? []);
  const [releases, setReleases] = useState<Map<string, ReleaseRecord>>(() => {
    const saved = loadLS<[string, ReleaseRecord][]>(LS_RELEASES, orgId);
    return saved ? new Map(saved) : new Map();
  });
  const [sendLogs, setSendLogs] = useState<Map<string, SendRecord[]>>(() => {
    const saved = loadLS<[string, SendRecord[]][]>(LS_SEND_LOGS, orgId);
    return saved ? new Map(saved) : new Map();
  });
  const [previousMoldRows, setPreviousMoldRows] = useState<MoldReadinessRow[]>([]);
  const [moldConflicts, setMoldConflicts] = useState<Map<string, string[]>>(new Map());
  // Track which session we've already loaded from Supabase to avoid redundant fetches
  const loadedSessionId = useRef<string | null>(null);
  // When a session is loaded from Supabase but localStorage cache is stale, store the
  // input_rows here so we can auto-recompute mold rows once molds finish loading.
  const pendingRecompute = useRef<PlanInputRow[] | null>(null);

  // ── Load from Supabase on mount (fall back to localStorage if none found) ──
  useEffect(() => {
    if (!user) return;

    async function loadFromSupabase() {
      try {
        const dbSession = await fetchReadinessSession(orgId);
        if (!dbSession) return; // keep localStorage data

        // Avoid redundant loads if we already have this session
        if (loadedSessionId.current === dbSession.id) return;
        loadedSessionId.current = dbSession.id;

        const [dbReleases, dbSendRecords] = await Promise.all([
          fetchReleases(dbSession.id),
          fetchSendRecords(dbSession.id),
        ]);

        const releasesMap = new Map<string, ReleaseRecord>(
          dbReleases.map(r => [r.released_for_date, r]),
        );
        const sendLogsMap = new Map<string, SendRecord[]>();
        dbSendRecords.forEach(r => {
          const existing = sendLogsMap.get(r.sent_for_date) ?? [];
          sendLogsMap.set(r.sent_for_date, [...existing, r]);
        });

        setSession(dbSession);
        setReleases(releasesMap);
        setSendLogs(sendLogsMap);
        // mold rows are computed — restore from localStorage cache if session matches
        const lsSession = loadLS<ReadinessSession>(LS_SESSION, orgId);
        if (lsSession?.id !== dbSession.id) {
          setMoldRowsState([]);
          setUnmappedItemsState([]);
          // Flag for auto-recompute once molds are available (e.g. fresh browser, no cache)
          pendingRecompute.current = dbSession.input_rows;
        }
      } catch (err) {
        console.error('[readiness] Supabase load failed, using localStorage', err);
      }
    }

    loadFromSupabase();
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Persist to localStorage on every change ────────────────────────────────
  useEffect(() => { saveLS(LS_SESSION,   orgId, session); },          [session, orgId]);
  useEffect(() => { saveLS(LS_MOLD_ROWS, orgId, moldRowsState); },    [moldRowsState, orgId]);
  useEffect(() => { saveLS(LS_UNMAPPED,  orgId, unmappedItemsState); },[unmappedItemsState, orgId]);
  useEffect(() => { saveLS(LS_RELEASES,  orgId, [...releases]); },     [releases, orgId]);
  useEffect(() => { saveLS(LS_SEND_LOGS, orgId, [...sendLogs]); },     [sendLogs, orgId]);

  // ── Readiness computation ──────────────────────────────────────────────────
  const computeReadiness = useCallback((
    moldId: string,
    moldsList: Mold[],
    pmPlansList: PMPlan[],
  ): { readiness: MoldReadiness; reasons: ReadinessReason[]; openWoIds: string[] } => {
    try {
      const mold = moldsList.find(m => m.id === moldId);
      if (!mold) return { readiness: 'unknown', reasons: [{ text: 'Mold not found', type: 'data_error' }], openWoIds: [] };

      const moldWOs = getWorkOrdersByMold(moldId);
      const openWOs = moldWOs.filter(wo => wo.status !== 'closed');
      const computedStatus = computeMoldStatus(mold, openWOs);
      const reasons: ReadinessReason[] = [];
      const openWoIds = openWOs.map(wo => wo.id);

      let hasBlockingIssue = false;
      let hasRiskIssue = false;

      // Blocking WO impacts → Not Ready
      const blockingWOs = openWOs.filter(wo => ['line_down', 'safety', 'quality'].includes(wo.impact));
      blockingWOs.forEach(wo => {
        const impactLabels: Record<string, string> = { line_down: 'Line Down', safety: 'Safety', quality: 'Quality' };
        reasons.push({ text: `${impactLabels[wo.impact]} — ${wo.wo_id}: ${wo.problem_description}`, type: 'blocking_wo', link: `/work-orders/${wo.id}` });
        hasBlockingIssue = true;
      });

      if (computedStatus === 'on_hold') {
        reasons.push({ text: 'Mold is On Hold', type: 'blocked_status', link: `/molds/${mold.id}` });
        hasBlockingIssue = true;
      }
      if (computedStatus === 'blocked' && blockingWOs.length === 0) {
        reasons.push({ text: 'Mold is blocked', type: 'blocked_status', link: `/molds/${mold.id}` });
        hasBlockingIssue = true;
      }
      if (computedStatus === 'in_maintenance') {
        const activeWO = openWOs.filter(wo => wo.type === 'preventive' || wo.type === 'corrective')[0];
        const woRef = activeWO ? ` — ${activeWO.wo_id}` : '';
        const woDesc = activeWO?.problem_description ? ` (${activeWO.problem_description})` : '';
        reasons.push({ text: `In Maintenance${woRef}${woDesc}`, type: 'in_maintenance', link: activeWO ? `/work-orders/${activeWO.id}` : `/molds/${mold.id}` });
        hasBlockingIssue = true;
      }

      // PM due from real pm_plans
      const pmPlan = pmPlansList.find(p => p.mold_id === moldId);
      const pmDue = computePMDue(mold, pmPlan);
      if (pmDue.isOverdue && pmDue.delta != null) {
        reasons.push({ text: `PM overdue by ${Math.abs(pmDue.delta).toLocaleString()} shots`, type: 'pm_overdue', link: `/molds/${mold.id}` });
        hasBlockingIssue = true;
      } else if (!pmDue.isOverdue && pmDue.delta != null && pmDue.delta < 5000) {
        reasons.push({ text: `PM due in ${pmDue.delta.toLocaleString()} shots`, type: 'pm_due_soon', link: `/molds/${mold.id}` });
        hasRiskIssue = true;
      }

      // Blocked WOs
      const blockedWOs = openWOs.filter(wo => wo.status === 'blocked');
      blockedWOs.forEach(wo => {
        const reasonMap: Record<string, string> = { parts: 'Waiting parts', qa_hold: 'QA Hold', supplier: 'Supplier', waiting_approval: 'Waiting approval', no_manpower: 'No manpower' };
        const reason = wo.blocked_reason ? reasonMap[wo.blocked_reason] || wo.blocked_reason : 'Blocked';
        reasons.push({ text: `${reason} — ${wo.wo_id}`, type: 'wo_blocked', link: `/work-orders/${wo.id}` });
        if (wo.blocked_reason === 'parts') hasBlockingIssue = true;
        else hasRiskIssue = true;
      });

      // Spare parts availability — check critical parts linked to this mold
      const linkedParts = getPartsByMold(moldId);
      linkedParts.forEach(mp => {
        if (!mp.is_common_failure_part || !mp.part) return;
        const part = mp.part as SparePart;
        const available = getAvailableStock(part);
        if (available <= 0) {
          reasons.push({ text: `Out of stock: ${part.name}`, type: 'parts_out', link: `/maintenance/spare-parts` });
          hasBlockingIssue = true;
        } else if (part.reorder_point != null && available <= part.reorder_point) {
          reasons.push({ text: `Low stock: ${part.name} (${available} available, reorder at ${part.reorder_point})`, type: 'parts_low', link: `/maintenance/spare-parts` });
          hasRiskIssue = true;
        }
      });

      let readiness: MoldReadiness = 'ready';
      if (hasBlockingIssue || computedStatus === 'blocked' || computedStatus === 'on_hold') readiness = 'not_ready';
      else if (hasRiskIssue || computedStatus === 'in_maintenance') readiness = 'at_risk';

      return { readiness, reasons, openWoIds };
    } catch {
      return { readiness: 'unknown', reasons: [{ text: 'Data missing — unable to compute readiness', type: 'data_error', link: `/molds/${moldId}` }], openWoIds: [] };
    }
  }, [getWorkOrdersByMold, getPartsByMold]);

  // ── generateRequirements ──────────────────────────────────────────────────
  const generateRequirements = useCallback((rows: PlanInputRow[]) => {
    const resolved: ResolvedRequirement[] = [];
    const unmapped: UnmappedItem[] = [];
    const moldRowsMap = new Map<string, MoldReadinessRow>();

    rows.forEach(row => {
      let targetMolds: Mold[];
      if (row.resolved_mold_id) {
        const mold = molds.find(m => m.id === row.resolved_mold_id);
        targetMolds = mold ? [mold] : [];
      } else {
        targetMolds = molds.filter(m => m.part_numbers?.includes(row.part_number));
      }

      if (targetMolds.length === 0) {
        unmapped.push({ part_number: row.part_number, requested_date: row.requested_date });
        return;
      }

      targetMolds.forEach(mold => {
        resolved.push({ requested_date: row.requested_date, part_number: row.part_number, mold_id: mold.id, mold_name: mold.name });

        const key = `${mold.id}_${row.requested_date}`;
        if (!moldRowsMap.has(key)) {
          const openWOs = getWorkOrdersByMold(mold.id).filter(wo => wo.status !== 'closed');
          const computedStatus = computeMoldStatus(mold, openWOs);
          const { readiness, reasons, openWoIds } = computeReadiness(mold.id, molds, pmPlans);
          moldRowsMap.set(key, {
            mold_id: mold.id, mold_name: mold.name, mold_display_id: mold.mold_id,
            location: getLocationDisplay(mold), computed_status: computedStatus,
            readiness, reasons, part_number: row.part_number,
            requested_date: row.requested_date, open_wo_ids: openWoIds,
          });
        }
      });
    });

    const inputHash = hashInputRows(rows);
    const prevVersion = session?.version || 0;
    const isNewPlan = !session || session.input_hash !== inputHash;
    // Always use a proper UUID so the session can be persisted to Supabase
    const sessionId = (!isNewPlan && session?.id && isUUID(session.id))
      ? session!.id
      : crypto.randomUUID();

    const newSession: ReadinessSession = {
      id: sessionId,
      created_at: isNewPlan ? new Date().toISOString() : session!.created_at,
      created_by: user?.id || '',
      updated_at: new Date().toISOString(),
      version: isNewPlan ? prevVersion + 1 : prevVersion,
      input_rows: rows,
      resolved_requirements: resolved,
      input_hash: inputHash,
    };

    // Build conflicts map: molds needed on 2+ different dates
    const moldDateMap = new Map<string, Set<string>>();
    moldRowsMap.forEach((row, key) => {
      const existing = moldDateMap.get(row.mold_id) ?? new Set<string>();
      existing.add(row.requested_date);
      moldDateMap.set(row.mold_id, existing);
    });
    const newConflicts = new Map<string, string[]>();
    moldDateMap.forEach((dates, moldId) => {
      if (dates.size > 1) newConflicts.set(moldId, Array.from(dates).sort());
    });

    if (moldRowsState.length > 0) setPreviousMoldRows([...moldRowsState]);

    setSession(newSession);
    setReleases(new Map());
    setUnmappedItemsState(unmapped);
    setMoldRowsState(Array.from(moldRowsMap.values()));
    setMoldConflicts(newConflicts);
    loadedSessionId.current = sessionId;

    // Persist to Supabase (fire and forget)
    if (user?.id && orgId) {
      upsertReadinessSession(newSession, orgId).catch(err =>
        console.error('[readiness] upsertReadinessSession failed', err),
      );
    }
  }, [computeReadiness, getWorkOrdersByMold, session, moldRowsState, molds, pmPlans, user, orgId]);

  // ── Auto-recompute mold rows after loading a Supabase session with no cache ──
  // Fires once molds finish loading; pendingRecompute is set by loadFromSupabase.
  useEffect(() => {
    if (!pendingRecompute.current || moldsLoading || molds.length === 0) return;
    const rows = pendingRecompute.current;
    pendingRecompute.current = null;
    generateRequirements(rows);
  }, [moldsLoading, molds, generateRequirements]); // eslint-disable-line react-hooks/exhaustive-deps

  const clearSession = useCallback(() => {
    setSession(null);
    setReleases(new Map());
    setMoldRowsState([]);
    setUnmappedItemsState([]);
    setSendLogs(new Map());
    setPreviousMoldRows([]);
    setMoldConflicts(new Map());
    clearLS(orgId);
  }, [orgId]);

  const releaseForDate = useCallback((date: string) => {
    if (!session) return;
    const existingRelease = releases.get(date);
    const now = new Date().toISOString();
    const newId = crypto.randomUUID();

    const record: ReleaseRecord = {
      id: existingRelease?.id ?? newId,
      readiness_session_id: session.id,
      released_for_date: date,
      released_at: now,
      released_by: user?.name || '',
      status: 'released',
    };

    setReleases(prev => {
      const next = new Map(prev);
      next.set(date, record);
      return next;
    });

    // Persist to Supabase (fire and forget) — only if session has a valid UUID
    if (user?.id && isUUID(session.id)) {
      if (existingRelease && isUUID(existingRelease.id)) {
        updateRelease(existingRelease.id, { status: 'released', released_at: now, released_by: user.id }).catch(err =>
          console.error('[readiness] updateRelease failed', err),
        );
      } else {
        createRelease({
          id: newId,
          readiness_session_id: session.id,
          released_for_date: date,
          released_at: now,
          released_by: user.id, // FK to profiles
          status: 'released',
        }).catch(err => console.error('[readiness] createRelease failed', err));
      }
    }
  }, [session, releases, user]);

  const needsRereleaseForDate = useCallback((date: string): boolean => {
    const rel = releases.get(date);
    if (!rel) return false;
    // Only flag if readiness changed since the last send for this date.
    // If no send exists yet, nothing was communicated — no re-release needed.
    const logs = sendLogs.get(date);
    if (!logs || logs.length === 0) return false;
    // Compare current rows against last-sent snapshot
    const lastSend = logs[logs.length - 1];
    const dateRows = moldRowsState.filter(r => r.requested_date === date);
    for (const row of dateRows) {
      const snap = lastSend.snapshot.find(s => s.mold_id === row.mold_id);
      if (!snap || snap.readiness !== row.readiness) return true;
    }
    for (const snap of lastSend.snapshot) {
      if (!dateRows.find(r => r.mold_id === snap.mold_id)) return true;
    }
    return false;
  }, [releases, sendLogs, moldRowsState]);

  const sendReadinessForDate = useCallback((date: string, sendAnywayReason?: string) => {
    if (!session) return;
    const dateRows = moldRowsState.filter(r => r.requested_date === date);
    const snapshot: SendSnapshot[] = dateRows.map(r => ({
      mold_id: r.mold_id, mold_display_id: r.mold_display_id, mold_name: r.mold_name,
      operational_status: r.computed_status, readiness: r.readiness,
      reasons: r.reasons.map(reason => reason.text), open_wo_ids: r.open_wo_ids,
    }));

    const sendId = crypto.randomUUID();
    const now = new Date().toISOString();

    const record: SendRecord = {
      id: sendId,
      readiness_session_id: session.id,
      sent_for_date: date,
      sent_at: now,
      sent_by: user?.name || '',
      plan_version: session.version,
      ready_count:     dateRows.filter(r => r.readiness === 'ready').length,
      at_risk_count:   dateRows.filter(r => r.readiness === 'at_risk').length,
      not_ready_count: dateRows.filter(r => r.readiness === 'not_ready' || r.readiness === 'unknown').length,
      send_anyway_reason: sendAnywayReason,
      snapshot,
    };

    setSendLogs(prev => {
      const next = new Map(prev);
      next.set(date, [...(next.get(date) || []), record]);
      return next;
    });
    releaseForDate(date);

    // Persist to Supabase (fire and forget) — only if session has a valid UUID
    if (user?.id && isUUID(session.id)) {
      createSendRecord({
        id: sendId,
        readiness_session_id: session.id,
        sent_for_date: date,
        sent_at: now,
        sent_by: user.id, // FK to profiles
        plan_version: session.version,
        ready_count:     record.ready_count,
        at_risk_count:   record.at_risk_count,
        not_ready_count: record.not_ready_count,
        send_anyway_reason: sendAnywayReason,
        snapshot,
      }).catch(err => console.error('[readiness] createSendRecord failed', err));
    }
  }, [session, moldRowsState, releaseForDate, user]);

  const hasReadinessChangedSinceSend = useCallback((date: string): boolean => {
    const logs = sendLogs.get(date);
    if (!logs || logs.length === 0) return false;
    const lastSend = logs[logs.length - 1];
    const dateRows = moldRowsState.filter(r => r.requested_date === date);
    for (const row of dateRows) {
      const snap = lastSend.snapshot.find(s => s.mold_id === row.mold_id);
      if (!snap || snap.readiness !== row.readiness) return true;
    }
    for (const snap of lastSend.snapshot) {
      if (!dateRows.find(r => r.mold_id === snap.mold_id)) return true;
    }
    return false;
  }, [sendLogs, moldRowsState]);

  const getSendLogsForDate = useCallback((date: string) => sendLogs.get(date) || [], [sendLogs]);

  const getPlanDelta = useCallback((): PlanDelta | null => {
    if (previousMoldRows.length === 0) return null;
    const prevKeys = new Set(previousMoldRows.map(r => `${r.mold_id}_${r.requested_date}`));
    const currKeys = new Set(moldRowsState.map(r => `${r.mold_id}_${r.requested_date}`));

    const added   = moldRowsState.filter(r => !prevKeys.has(`${r.mold_id}_${r.requested_date}`))
      .map(r => ({ mold_id: r.mold_id, mold_display_id: r.mold_display_id, mold_name: r.mold_name, date: r.requested_date }));
    const removed = previousMoldRows.filter(r => !currKeys.has(`${r.mold_id}_${r.requested_date}`))
      .map(r => ({ mold_id: r.mold_id, mold_display_id: r.mold_display_id, mold_name: r.mold_name, date: r.requested_date }));
    const changed: PlanDelta['changed_readiness'] = [];
    moldRowsState.forEach(r => {
      const prev = previousMoldRows.find(p => p.mold_id === r.mold_id && p.requested_date === r.requested_date);
      if (prev && prev.readiness !== r.readiness)
        changed.push({ mold_id: r.mold_id, mold_display_id: r.mold_display_id, mold_name: r.mold_name, date: r.requested_date, from: prev.readiness, to: r.readiness });
    });

    if (!added.length && !removed.length && !changed.length) return null;
    return { added_molds: added, removed_molds: removed, changed_readiness: changed };
  }, [previousMoldRows, moldRowsState]);

  const value = useMemo(() => ({
    session, releases, moldRows: moldRowsState, unmappedItems: unmappedItemsState,
    sendLogs, previousMoldRows, moldsLoading, molds, moldConflicts,
    generateRequirements, clearSession, releaseForDate, needsRereleaseForDate,
    sendReadinessForDate, hasReadinessChangedSinceSend, getSendLogsForDate, getPlanDelta,
  }), [session, releases, moldRowsState, unmappedItemsState, sendLogs, previousMoldRows, moldsLoading,
    moldConflicts, generateRequirements, clearSession, releaseForDate, needsRereleaseForDate,
    sendReadinessForDate, hasReadinessChangedSinceSend, getSendLogsForDate, getPlanDelta]);

  return (
    <ReadinessContext.Provider value={value}>
      {children}
    </ReadinessContext.Provider>
  );
}

export function useReadiness() {
  const context = useContext(ReadinessContext);
  if (!context) throw new Error('useReadiness must be used within a ReadinessProvider');
  return context;
}
